import React, { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Icon } from '@/shared/icons'
/**
 * Form for configuring Website Widget
 *
 * Flow: Configure widget → Create channel → Show embed script
 *
 * @example
 * <WidgetForm
 *   onComplete={(data) => handleAuthComplete(data)}
 * />
 */
export interface WidgetFormProps {
  /** Callback when configuration is complete */
  onComplete: (data: WidgetData) => void;
}

export interface WidgetData {
  providerId: string;
  name: string;
  identifier: string;  // Domain do site
  config: {
    brandColor?: string;
    position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
    welcomeMessage?: string;
    agentName?: string;
  };
  credentials: Record<string, never>;  // Widget não precisa de credentials
}

type ConfigStep = 'basic' | 'customization' | 'complete';

export const WidgetForm: React.FC<WidgetFormProps> = ({
  onComplete,
}) => {
  const [step, setStep] = useState<ConfigStep>('basic');
  const [channelName, setChannelName] = useState('');
  const [domain, setDomain] = useState('');
  const [brandColor, setBrandColor] = useState('#3B82F6');
  const [position, setPosition] = useState<'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'>('bottom-right');
  const [welcomeMessage, setWelcomeMessage] = useState('Olá! Como posso ajudar?');
  const [agentName, setAgentName] = useState('Atendimento');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleContinue = () => {
    // Validação
    if (!channelName || !domain) {
      setErrorMessage('Preencha todos os campos obrigatórios');
      return;
    }

    if (channelName.trim().length < 2) {
      setErrorMessage('Nome do canal deve ter pelo menos 2 caracteres');
      return;
    }

    // Validação básica de domínio (pode ser URL completa ou apenas domínio)
    const domainPattern = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i;
    const urlPattern = /^https?:\/\/([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}/i;

    if (!domainPattern.test(domain) && !urlPattern.test(domain)) {
      setErrorMessage('Domínio inválido. Use o formato: exemplo.com.br ou https://exemplo.com.br');
      return;
    }

    setErrorMessage(null);
    setStep('customization');
  };

  const handleComplete = () => {
    onComplete({
      providerId: 'website-widget',
      name: channelName,
      identifier: domain,
      config: {
        brandColor,
        position,
        welcomeMessage,
        agentName,
      },
      credentials: {},  // Widget não precisa de credentials
    });
  };

  return (
    <div className="space-y-6">
      {/* Step 1: Basic Configuration */}
      {step === 'basic' && (
        <div className="space-y-4">
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: '#1e293b' }}
            >
              Nome do Canal *
            </label>
            <Input
              type="text"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              placeholder="Ex: Chat do Site"
              className="w-full"
            />
            <p
              className="text-xs mt-1"
              style={{ color: '#64748b' }}
            >
              Nome para identificar este widget internamente
            </p>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: '#1e293b' }}
            >
              Domínio do Site *
            </label>
            <div className="relative">
              <Icon name="globe" size={18}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none"
                style={{ color: '#64748b' }} />
              <Input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="exemplo.com.br ou https://exemplo.com.br"
                className="w-full pl-10"
              />
            </div>
            <p
              className="text-xs mt-1"
              style={{ color: '#64748b' }}
            >
              Domínio onde o widget será embedado (para segurança)
            </p>
          </div>

          {/* Info Box */}
          <div
            className="rounded-lg p-4 border-l-4"
            style={{
              backgroundColor: '#eff6ff',
              borderColor: '#3b82f6',
            }}
          >
            <div className="flex items-start gap-3">
              <Icon name="code" size={20} style={{ color: '#3b82f6' }} className="flex-shrink-0 mt-0.5" />
              <div>
                <h4
                  className="text-sm font-semibold mb-1"
                  style={{ color: '#1e3a8a' }}
                >
                  Como funciona?
                </h4>
                <p
                  className="text-xs"
                  style={{ color: '#1e40af' }}
                >
                  Após a criação, você receberá um código JavaScript para embedar no seu site.
                  Seus visitantes poderão iniciar conversas em tempo real com seus agentes.
                </p>
              </div>
            </div>
          </div>

          {errorMessage && (
            <div
              className="rounded-lg p-3 text-sm"
              style={{
                backgroundColor: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fecaca',
              }}
            >
              {errorMessage}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleContinue}
              className="px-6"
            >
              Continuar
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Customization */}
      {step === 'customization' && (
        <div className="space-y-4">
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: '#1e293b' }}
            >
              Cor Principal
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                className="h-10 w-20 rounded cursor-pointer"
              />
              <Input
                type="text"
                value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                placeholder="#3B82F6"
                className="flex-1"
              />
            </div>
            <p
              className="text-xs mt-1"
              style={{ color: '#64748b' }}
            >
              Cor usada no botão e header do widget
            </p>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: '#1e293b' }}
            >
              Posição na Tela
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'bottom-right' as const, label: 'Inferior Direita' },
                { value: 'bottom-left' as const, label: 'Inferior Esquerda' },
                { value: 'top-right' as const, label: 'Superior Direita' },
                { value: 'top-left' as const, label: 'Superior Esquerda' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setPosition(option.value)}
                  className="px-4 py-2 text-sm rounded-lg border transition-colors"
                  style={{
                    backgroundColor: position === option.value ? '#eff6ff' : '#ffffff',
                    borderColor: position === option.value ? '#3b82f6' : '#e5e7eb',
                    color: position === option.value ? '#1e40af' : '#64748b',
                  }}
                  data-testid={`widget-position-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: '#1e293b' }}
            >
              Mensagem de Boas-Vindas
            </label>
            <div className="relative">
              <Icon name="message-square" size={18}
                className="absolute left-3 top-3 z-10 pointer-events-none"
                style={{ color: '#64748b' }} />
              <Textarea
                value={welcomeMessage}
                onChange={(e) => setWelcomeMessage(e.target.value)}
                placeholder="Olá! Como posso ajudar?"
                rows={3}
                className="w-full pl-10"
                resize="none"
              />
            </div>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: '#1e293b' }}
            >
              Nome do Agente
            </label>
            <div className="relative">
              <Icon name="user" size={18}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none"
                style={{ color: '#64748b' }} />
              <Input
                type="text"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                placeholder="Atendimento"
                className="w-full pl-10"
              />
            </div>
            <p
              className="text-xs mt-1"
              style={{ color: '#64748b' }}
            >
              Nome exibido no header do widget
            </p>
          </div>

          {/* Preview Box */}
          <div
            className="rounded-lg p-4 border"
            style={{
              backgroundColor: '#f9fafb',
              borderColor: '#e5e7eb',
            }}
          >
            <div className="flex items-start gap-3 mb-3">
              <Icon name="check-circle" size={20} style={{ color: '#10b981' }} className="flex-shrink-0 mt-0.5" />
              <div>
                <h4
                  className="text-sm font-semibold mb-1"
                  style={{ color: '#1e293b' }}
                >
                  Preview da Configuração
                </h4>
                <p
                  className="text-xs"
                  style={{ color: '#64748b' }}
                >
                  Assim ficará o widget no seu site:
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div
                className="w-3 h-3 rounded-full flex-shrink-0 mt-1"
                style={{ backgroundColor: brandColor }}
              />
              <div className="space-y-1">
                <p className="text-xs font-medium" style={{ color: '#1e293b' }}>
                  {agentName}
                </p>
                <p className="text-xs" style={{ color: '#64748b' }}>
                  {welcomeMessage}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-2">
            <Button
              onClick={() => setStep('basic')}
              variant="secondary"
            >
              Voltar
            </Button>
            <Button
              onClick={handleComplete}
              className="px-6"
            >
              Criar Widget
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

WidgetForm.displayName = 'WidgetForm';
